/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import ShieldIcon from '@atlaskit/icon/core/shield';
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import Tag from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useKnowledgeSourceIconMap } from '@atlassian/conversation-assistant-service-api/useKnowledgeSourceIconMap';
import { useAssistanceService } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { getKnowledgeSourceIcon } from '@atlassian/conversation-assistant-utils/knowledge-source-icons';

import type { PromptCategory } from '../../../../controllers/prompt-cards';
import { getUseCaseIcon } from '../../../../controllers/prompt-use-case-icons';

import { i18n } from './messages';
import { generateShareLink } from './utils/share-link';

type SelectOption = { label: string; value: string };

const styles = cssMap({
	headerFlex: {
		flexDirection: 'row-reverse',
		width: '100%',
	},
	card: {
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		position: 'relative',
		maxHeight: '22rem',
		overflowY: 'auto',
	},
	promptTitle: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		marginBottom: token('space.100'),
	},
	useCaseIconContainer: {
		width: '40px',
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
	promptText: {
		color: token('color.text'),
	},
	infoSection: {
		marginTop: token('space.200'),
		paddingTop: token('space.200'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	infoRow: {
		display: 'flex',
		gap: token('space.100'),
		marginBottom: token('space.100'),
		alignItems: 'flex-start',
		justifyContent: 'space-between',
	},
	infoLabel: {
		minWidth: '140px',
		flexShrink: 0,
	},
	infoValue: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'flex-start',
		flexWrap: 'wrap',
		flex: 1,
	},
	appIcons: {
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'flex-end',
		alignItems: 'center',
	},
	appTagIcon: {
		paddingInline: token('space.025'),
	},
	appTagImage: {
		width: '12px',
		height: '12px',
	},
	copyButtonContainer: {
		width: '100%',
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.brand.boldest'),
		overflow: 'hidden',
	},
	privacyGroup: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.050'),
		paddingBottom: token('space.150'),
	},
	buttonRow: {
		display: 'flex',
		justifyContent: 'center',
	},
});

export type SharePromptModalProps = {
	isOpen: boolean;
	onClose: () => void;
	promptData: {
		title: string;
		prompt: string;
		useCase: SelectOption | null;
		role: SelectOption[];
		app: SelectOption[];
		shareId: string;
		isPrivate?: boolean;
	};
	organizationName?: string;
};

export const SharePromptModal = ({
	isOpen,
	onClose,
	promptData,
	organizationName,
}: SharePromptModalProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [copySuccess, setCopySuccess] = useState(false);
	const [shareError, setShareError] = useState<string | null>(null);
	const assistanceService = useAssistanceService();
	const { iconMap: knowledgeSourceIconMap, isLoading: isLoadingIcons } =
		useKnowledgeSourceIconMap(assistanceService);

	// Always show an icon - use the useCase icon if available, otherwise use the default GeneralPromptIcon
	const UseCaseIcon = getUseCaseIcon(promptData.useCase?.value as PromptCategory | undefined);
	const shareUrl = generateShareLink([promptData.shareId]);
	const { sendAnalyticsEvent } = useAnalytics();

	const handleCopyLink = useCallback(async () => {
		setShareError(null);
		try {
			const isSystemPrompt = promptData.shareId.startsWith('sys_');
			if (!isSystemPrompt && promptData.isPrivate !== false) {
				await assistanceService.updatePrompt(promptData.shareId, { isPrivate: false });
			}
			await navigator.clipboard.writeText(shareUrl);
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'copyPromptShareLink',
			});
			setCopySuccess(true);
			setTimeout(() => setCopySuccess(false), 2000);
		} catch {
			setShareError(formatMessage(i18n.errorShareFailed));
		}
	}, [
		shareUrl,
		promptData.isPrivate,
		promptData.shareId,
		assistanceService,
		formatMessage,
		sendAnalyticsEvent,
	]);

	const hasInformation =
		promptData.useCase || promptData.role.length > 0 || promptData.app.length > 0;

	return (
		<ModalTransition>
			{isOpen && (
				<Modal
					onClose={onClose}
					width="medium"
					shouldScrollInViewport
					shouldCloseOnOverlayClick={false}
				>
					<ModalHeader hasCloseButton>
						<ModalTitle>{formatMessage(i18n.sharePromptTitle)}</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<Stack space="space.200">
							<Box xcss={styles.card}>
								{/* Prompt Title with Icon */}
								<Box xcss={styles.promptTitle}>
									<Box xcss={styles.useCaseIconContainer}>
										<UseCaseIcon />
									</Box>
									<Text weight="semibold" size="medium">
										{promptData.title}
									</Text>
								</Box>

								{/* Prompt Text */}
								<Box xcss={styles.promptText}>
									<Text>{promptData.prompt}</Text>
								</Box>

								{/* Information Section */}
								{hasInformation && (
									<Stack space="space.200" xcss={styles.infoSection}>
										<Text weight="semibold" size="small">
											{formatMessage(i18n.informationLabel)}
										</Text>
										<Stack space="space.100">
											{promptData.useCase && (
												<Box xcss={styles.infoRow}>
													<Box xcss={styles.infoLabel}>
														<Text size="small" color="color.text.subtle">
															{formatMessage(i18n.useCaseLabel)}
														</Text>
													</Box>
													<Box xcss={styles.infoValue}>
														<Text size="small">{promptData.useCase.label}</Text>
													</Box>
												</Box>
											)}
											{promptData.role.length > 0 && (
												<Box xcss={styles.infoRow}>
													<Box xcss={styles.infoLabel}>
														<Text size="small" color="color.text.subtle">
															{formatMessage(i18n.roleLabel)}
														</Text>
													</Box>
													<Box xcss={styles.infoValue}>
														<Text size="small">
															{promptData.role.map((r) => r.label).join(', ')}
														</Text>
													</Box>
												</Box>
											)}
											{promptData.app.length > 0 && (
												<Box xcss={styles.infoRow}>
													<Box xcss={styles.infoLabel}>
														<Text size="small" color="color.text.subtle">
															{formatMessage(i18n.connectedAppsLabel)}
														</Text>
													</Box>
													<Box xcss={styles.infoValue}>
														<Inline space="space.050" xcss={styles.appIcons}>
															{promptData.app.map((app) => {
																// Use API icon if available, fallback to local icon
																const apiIconUrl = knowledgeSourceIconMap[app.value];
																const fallbackIcon = getKnowledgeSourceIcon(app.value, {
																	size: 12,
																});

																// Show skeleton while loading icons
																const iconElement = isLoadingIcons ? (
																	<Box xcss={styles.appTagIcon}>
																		<Skeleton
																			width="12px"
																			height="12px"
																			borderRadius={token('radius.xsmall')}
																		/>
																	</Box>
																) : apiIconUrl ? (
																	<Box xcss={styles.appTagIcon}>
																		<Box
																			as="img"
																			src={apiIconUrl}
																			alt={app.label}
																			xcss={styles.appTagImage}
																		/>
																	</Box>
																) : fallbackIcon ? (
																	<Box xcss={styles.appTagIcon}>{fallbackIcon}</Box>
																) : undefined;

																return (
																	<Tag
																		key={app.value}
																		text={app.label}
																		isRemovable={false}
																		elemBefore={iconElement}
																	/>
																);
															})}
														</Inline>
													</Box>
												</Box>
											)}
										</Stack>
									</Stack>
								)}
							</Box>

							{/* Copy Link Button */}
							<Box xcss={styles.buttonRow}>
								<Box xcss={styles.copyButtonContainer}>
									<Button appearance="primary" onClick={handleCopyLink} shouldFitContainer>
										<Inline alignBlock="center" alignInline="center" space="space.100">
											<LinkIcon label="" color="currentColor" />
											<Text color="color.text.inverse">
												{copySuccess
													? formatMessage(i18n.linkCopied)
													: formatMessage(i18n.copyLinkButton)}
											</Text>
										</Inline>
									</Button>
								</Box>
							</Box>

							{shareError && (
								<Text size="small" color="color.text.danger">
									{shareError}
								</Text>
							)}

							<Box xcss={styles.privacyGroup}>
								<ShieldIcon label="" color={token('color.icon.subtle')} />
								<Text size="small" color="color.text.subtle">
									{organizationName
										? formatMessage(i18n.privacyNote, { organizationName })
										: formatMessage(i18n.privacyNoteNoOrganization)}
								</Text>
							</Box>
						</Stack>
					</ModalBody>
				</Modal>
			)}
		</ModalTransition>
	);
};
